import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { Layout } from './components/Layout';
import { Spinner } from './components/ui';
import { Login } from './pages/Login';
import { Overview } from './pages/Overview';
import { Machines } from './pages/Machines';
import { MachineDetail } from './pages/MachineDetail';
import { Launch } from './pages/Launch';
import { Encoders, EncoderDetail } from './pages/Encoders';
import { Adopted } from './pages/Adopted';
import { Devices } from './pages/Devices';
import { Magewell } from './pages/Magewell';
import { Intercom } from './pages/Intercom';
import { Call } from './pages/Call';
import { LaunchFlow, FlowDetail } from './pages/Flows';
import { Costs } from './pages/Costs';
import { Recordings } from './pages/Recordings';
import { Admin } from './pages/Admin';
import { Account, ForcedPasswordChange } from './pages/Account';
import { ApiKeys } from './pages/ApiKeys';

function Protected() {
  const { user, loading } = useAuth();

  // Waiting for the session check. Rendering the login form here would flash it
  // at every already-signed-in user on every page load.
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  // A user still on an admin-issued password gets nothing else until they set
  // their own. Blocking here rather than nagging means the generated password,
  // which was transmitted to them by some other channel, has a short life.
  if (user.mustChangePassword) return <ForcedPasswordChange />;

  return <Layout />;
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginRoute />} />

          <Route element={<Protected />}>
            <Route index element={<Overview />} />
            <Route path="machines" element={<Machines />} />
            <Route path="machines/:instanceId" element={<MachineDetail />} />
            <Route path="launch" element={<Launch />} />
            <Route path="launch/encoder" element={<Launch encoder />} />
            <Route path="encoders" element={<Encoders />} />
            <Route path="encoders/:id" element={<EncoderDetail />} />
            <Route path="launch/flow" element={<LaunchFlow />} />
            <Route path="flows/:id" element={<FlowDetail />} />
            <Route path="adopted" element={<Adopted />} />
            <Route path="devices" element={<Devices />} />
            <Route path="magewell" element={<Magewell />} />
            <Route path="intercom" element={<Intercom />} />
            <Route path="call" element={<Call />} />
            <Route path="recordings" element={<Recordings />} />
            <Route path="costs" element={<Costs />} />
            <Route path="admin" element={<Admin />} />
            <Route path="account" element={<Account />} />
            <Route path="keys" element={<ApiKeys />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
